import { ClientLogger } from 'bernie-client';
import { SerializedData } from 'bernie-core';
import { KeyedObject } from 'bernie-core/dist/source';
import { getErrorString, Logger, NOOP_LOGGER } from 'bernie-logger';
import { Store } from 'bernie-plugin-mobx';
import { observable, ObservableMap } from 'mobx';
import { inject } from 'mobx-react';
import { LXCreateTripResponse } from '../types';
import {
  ActivityDetailBasic,
  ActivityDetailOffers,
  ActivityPriceType,
  ActivityRedemptionType
} from '../common/__generated__/api';
import { STORE_EVENTS } from './store-events';

const NUM_OF_DIGITS_AFTER_DECIMAL_POINT = 2;
const SWP_VALUE_ONE = 1;
const SWP_VALUE_TWO = 2;

// These values should only be set in controllers coming from the query
export interface TripCreationRequest {
  dxId: string;
  dxItemId: string;
  date: string;
  destination?: string | null;
  regionId: string;
  location?: string | null;
  startDate: string;
  endDate: string;
  redemptionType: string | null;
  swpCheckboxState: number;
  availableCount: number;
  hotelPickup: boolean;
  amount: number;
  tickets: CheckoutTicket[];
  uisExpandedSearch: string;
  uisDeal: string;
  promoType: string;
  promotionId?: string | null;
  uisRecommendedEntity: string;
  nonStdImageAllowed: boolean;
}

export interface CheckoutTicket {
  count: number;
  code: string;
  amount: number;
  promoId: string;
  travelerCount?: number;
}

export enum TRIP_CREATION_ERROR_CODES {
  INVENTORY_OOS,
  BOOKING_ERROR
}

inject('activityDetailStore');

export class TripCreationStore extends Store {
  public offerNotAvailableErrors: ObservableMap = observable.map({});

  public constructor(state: SerializedData = {}, logger: Logger | ClientLogger = NOOP_LOGGER) {
    super(state, logger);

    this.createTripV2 = this.createTripV2.bind(this);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, class-methods-use-this
  public hydrate(_data: SerializedData): void {
    // Do nothing
  }

  public createTrip(
    offer: ActivityDetailOffers.Offers,
    activity: ActivityDetailBasic.ActivityInfo,
    startDate: string,
    endDate: string,
    swpApplied: boolean,
    updateBookingLoaderState: (value: boolean) => void
  ): Promise<void> {
    const tickets: CheckoutTicket[] = [];
    const amount = this.populateTicketsAndCalculateAmount(offer, tickets);

    const tripRequest: TripCreationRequest = {
      tickets,
      amount,
      dxId: activity.activity.id,
      dxItemId: offer.id,
      date: offer.formattedAvailableTime,
      destination: activity.location.event.region.regionName,
      regionId: activity.location.event.region.regionId,
      location: activity.location.event.region.regionName,
      startDate,
      endDate,
      redemptionType: this.getRedemptionType(activity),
      swpCheckboxState: swpApplied ? SWP_VALUE_ONE : SWP_VALUE_TWO,
      availableCount: offer.count,
      hotelPickup: offer.hotelPickupAvailable,
      uisExpandedSearch: 'false',
      uisDeal: this.getUisDeal(offer),
      promoType: '',
      promotionId: offer.promoId,
      uisRecommendedEntity: 'false',
      nonStdImageAllowed: false
    };

    return fetch('/LXCreateTrip', {
      method: 'POST',
      body: JSON.stringify(tripRequest),
      headers: {
        // eslint-disable-next-line quote-props
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    })
      .then((response) => response.json())
      .then(
        (data) => {
          if (data && data.tripid) {
            window.location.href = `/MultiItemCheckout?tripid=${data.tripid}`;
          } else if (data.errors) {
            data.errors.forEach((error: KeyedObject) => {
              if (error.errors && error.errors.length) {
                error.errors.forEach((internalError: KeyedObject) => {
                  const descriptionText = JSON.stringify(internalError.description);
                  if (descriptionText.indexOf('9003 Unable to create product due to lack of inventory') > 0) {
                    this.offerNotAvailableErrors.set(offer.id, TRIP_CREATION_ERROR_CODES.INVENTORY_OOS);
                  } else {
                    this.offerNotAvailableErrors.set(offer.id, TRIP_CREATION_ERROR_CODES.BOOKING_ERROR);
                  }
                  this.logger.logEvent(STORE_EVENTS.TRIP_ID_NOT_RETURNED, tripRequest, descriptionText);
                });
              }
            });
            updateBookingLoaderState(false);
          } else {
            this.logger.logEvent(STORE_EVENTS.TRIP_ID_NOT_RETURNED, tripRequest);
          }
          updateBookingLoaderState(false);
        },
        (error) => {
          this.logger.logEvent(STORE_EVENTS.CREATE_TRIP_FAILURE, tripRequest, getErrorString(error));
          updateBookingLoaderState(false);
          this.offerNotAvailableErrors.set(offer.id, TRIP_CREATION_ERROR_CODES.BOOKING_ERROR);
        }
      )
      .catch((error) => {
        this.logger.logEvent(STORE_EVENTS.CREATE_TRIP_FAILURE, tripRequest, getErrorString(error));
        this.offerNotAvailableErrors.set(offer.id, TRIP_CREATION_ERROR_CODES.BOOKING_ERROR);
        updateBookingLoaderState(false);
      });
  }

  public createTripV2(tripRequest: string): Promise<number | void> {
    return fetch('/LXCreateTrip', {
      method: 'POST',
      body: tripRequest,
      headers: {
        // eslint-disable-next-line quote-props
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    })
      .then((response) => response.json())
      .then((data: LXCreateTripResponse) => {
        let errorCode = -1;

        if (data.tripid) {
          window.location.href = `/MultiItemCheckout?tripid=${data.tripid}`;
        }
        if (data.errors) {
          data.errors.forEach((errorInfo) => {
            errorInfo.errors.forEach((errorDetailInfo) => {
              const descriptionText = JSON.stringify(errorDetailInfo.description);

              if (descriptionText.indexOf('9003 Unable to create product due to lack of inventory') > 0) {
                errorCode = TRIP_CREATION_ERROR_CODES.INVENTORY_OOS;
              } else {
                errorCode = TRIP_CREATION_ERROR_CODES.BOOKING_ERROR;
              }
              this.logger.logEvent(STORE_EVENTS.TRIP_ID_NOT_RETURNED, tripRequest, descriptionText);
            });
          });
        }

        return errorCode;
      })
      .catch((error) => {
        this.logger.logEvent(STORE_EVENTS.CREATE_TRIP_FAILURE, tripRequest, getErrorString(error));
      });
  }

  // eslint-disable-next-line class-methods-use-this
  private getUisDeal(offer: ActivityDetailOffers.Offers): string {
    if (offer.priceMetaData?.discountType !== 'STRIKE_OUT' && offer.priceMetaData?.discountType !== 'GUEST') {
      if (offer.priceMetaData?.discountType === 'MOD') {
        return 'MOD';
      }

      return 'MIP';
    }

    return 'NONE';
  }

  // eslint-disable-next-line class-methods-use-this
  private getRedemptionType(activity: ActivityDetailBasic.ActivityInfo): string | null {
    if (activity.activity.redemption?.type) {
      return activity.activity.redemption.type === ActivityRedemptionType.VOUCHER_LESS ? 'Voucherless' : 'Print';
    }

    return null;
  }

  // eslint-disable-next-line class-methods-use-this
  private populateTicketsAndCalculateAmount(offer: ActivityDetailOffers.Offers, tickets: CheckoutTicket[]): number {
    let amount = 0;
    offer.ticketTypes
      .filter((t: ActivityDetailOffers.TicketTypes) => t.defaultSelectedCount > 0)
      .forEach((t: ActivityDetailOffers.TicketTypes) => {
        const currentCount = parseInt(t.defaultSelectedCount.toString(), 10);
        const price = t.prices.find((it) => it.selectedCount === currentCount);
        if (price) {
          if (offer.priceType === ActivityPriceType.NORMAL || offer.priceType === ActivityPriceType.VOLUME_BASED) {
            tickets.push({
              count: currentCount,
              code: t.ticketKey,
              amount: Number(price.perTravelerPrice.lead.amount.toFixed(NUM_OF_DIGITS_AFTER_DECIMAL_POINT)),
              promoId: t.promoId || ''
            });
          } else {
            tickets.push({
              count: 1,
              code: t.ticketKey,
              amount: Number(price.totalPrice.lead.amount.toFixed(NUM_OF_DIGITS_AFTER_DECIMAL_POINT)),
              promoId: t.promoId || '',
              travelerCount: currentCount
            });
          }
          amount += Number(price.totalPrice.lead.amount.toFixed(NUM_OF_DIGITS_AFTER_DECIMAL_POINT));
        }
      });

    return amount;
  }
}
